/**
 * Curated instrument leaf addresses for Phase 2 Learn.
 * Public path: `/track/{n}/instrument/preset/{slash/leaf}`
 * Resolved against the slot's current instrument kind.
 */

export type InstrumentPresetLeaf = {
  /** Dotted path into the active instrument payload. */
  leaf: string;
  range: [number, number];
  label: string;
};

export type InstrumentLeafKind =
  | "synth"
  | "wavetable"
  | "sampler"
  | "drums"
  | "sf2";

const SYNTH_LEAVES: InstrumentPresetLeaf[] = [
  { leaf: "glideMs", range: [0, 500], label: "Glide" },
  { leaf: "osc1.octave", range: [-3, 3], label: "Osc1 octave" },
  { leaf: "osc1.semis", range: [-12, 12], label: "Osc1 semis" },
  { leaf: "osc1.detuneCents", range: [-100, 100], label: "Osc1 detune" },
  { leaf: "osc1.level", range: [0, 1], label: "Osc1 level" },
  { leaf: "osc1.pwm", range: [0, 1], label: "Osc1 PWM" },
  { leaf: "osc2.octave", range: [-3, 3], label: "Osc2 octave" },
  { leaf: "osc2.semis", range: [-12, 12], label: "Osc2 semis" },
  { leaf: "osc2.detuneCents", range: [-100, 100], label: "Osc2 detune" },
  { leaf: "osc2.level", range: [0, 1], label: "Osc2 level" },
  { leaf: "osc2.pwm", range: [0, 1], label: "Osc2 PWM" },
  { leaf: "osc3.octave", range: [-3, 3], label: "Osc3 octave" },
  { leaf: "osc3.semis", range: [-12, 12], label: "Osc3 semis" },
  { leaf: "osc3.detuneCents", range: [-100, 100], label: "Osc3 detune" },
  { leaf: "osc3.level", range: [0, 1], label: "Osc3 level" },
  { leaf: "osc3.pwm", range: [0, 1], label: "Osc3 PWM" },
  { leaf: "fm.fmDepth", range: [0, 8], label: "FM depth" },
  { leaf: "fm.amDepth", range: [0, 1], label: "Ring" },
  { leaf: "fm.velocityToFm", range: [0, 1], label: "Vel → FM" },
  { leaf: "noise.level", range: [0, 1], label: "Noise" },
  { leaf: "filter.cutoffHz", range: [40, 12000], label: "Cutoff" },
  { leaf: "filter.resonance", range: [0, 1], label: "Resonance" },
  { leaf: "ampEnv.a", range: [0.001, 2], label: "Amp A" },
  { leaf: "ampEnv.d", range: [0.001, 2], label: "Amp D" },
  { leaf: "ampEnv.s", range: [0, 1], label: "Amp S" },
  { leaf: "ampEnv.r", range: [0.001, 3], label: "Amp R" },
  { leaf: "modEnv.a", range: [0.001, 2], label: "Mod A" },
  { leaf: "modEnv.d", range: [0.001, 2], label: "Mod D" },
  { leaf: "modEnv.s", range: [0, 1], label: "Mod S" },
  { leaf: "modEnv.r", range: [0.001, 3], label: "Mod R" },
  { leaf: "lfo.rateHz", range: [0, 20], label: "LFO rate" },
  { leaf: "lfo.depth", range: [0, 1], label: "LFO depth" },
];

const WAVETABLE_LEAVES: InstrumentPresetLeaf[] = [
  { leaf: "voices", range: [1, 9], label: "Voices" },
  { leaf: "unisonDetune", range: [0, 100], label: "Unison detune" },
  { leaf: "unisonSpread", range: [0, 1], label: "Unison spread" },
  { leaf: "unisonEdgeDecay", range: [0, 1], label: "Unison edge" },
  { leaf: "glideMs", range: [0, 500], label: "Glide" },
  { leaf: "osc1.position", range: [0, 1], label: "Osc1 position" },
  { leaf: "osc1.warp", range: [0, 1], label: "Osc1 warp" },
  { leaf: "osc1.octave", range: [-3, 3], label: "Osc1 octave" },
  { leaf: "osc1.semis", range: [-12, 12], label: "Osc1 semis" },
  { leaf: "osc1.detuneCents", range: [-100, 100], label: "Osc1 detune" },
  { leaf: "osc1.level", range: [0, 1], label: "Osc1 level" },
  { leaf: "osc2.position", range: [0, 1], label: "Osc2 position" },
  { leaf: "osc2.warp", range: [0, 1], label: "Osc2 warp" },
  { leaf: "osc2.octave", range: [-3, 3], label: "Osc2 octave" },
  { leaf: "osc2.semis", range: [-12, 12], label: "Osc2 semis" },
  { leaf: "osc2.detuneCents", range: [-100, 100], label: "Osc2 detune" },
  { leaf: "osc2.level", range: [0, 1], label: "Osc2 level" },
  { leaf: "pmDepth", range: [0, 4], label: "PM depth" },
  { leaf: "sub.octave", range: [-2, 0], label: "Sub octave" },
  { leaf: "sub.level", range: [0, 1], label: "Sub level" },
  { leaf: "noise.level", range: [0, 1], label: "Noise" },
  { leaf: "filter.cutoffHz", range: [40, 12000], label: "Cutoff" },
  { leaf: "filter.resonance", range: [0, 1], label: "Resonance" },
  { leaf: "ampEnv.a", range: [0.001, 2], label: "Amp A" },
  { leaf: "ampEnv.d", range: [0.001, 2], label: "Amp D" },
  { leaf: "ampEnv.s", range: [0, 1], label: "Amp S" },
  { leaf: "ampEnv.r", range: [0.001, 3], label: "Amp R" },
  { leaf: "modEnv.a", range: [0.001, 2], label: "Mod A" },
  { leaf: "modEnv.d", range: [0.001, 2], label: "Mod D" },
  { leaf: "modEnv.s", range: [0, 1], label: "Mod S" },
  { leaf: "modEnv.r", range: [0.001, 3], label: "Mod R" },
  { leaf: "lfo.rateHz", range: [0.01, 20], label: "LFO rate" },
  { leaf: "lfo.depth", range: [0, 1], label: "LFO depth" },
];

const SAMPLER_LEAVES: InstrumentPresetLeaf[] = [
  { leaf: "filter.cutoffHz", range: [40, 18000], label: "Cutoff" },
  { leaf: "filter.resonance", range: [0, 1], label: "Resonance" },
  { leaf: "ampEnv.a", range: [0.001, 2], label: "Amp A" },
  { leaf: "ampEnv.d", range: [0.001, 2], label: "Amp D" },
  { leaf: "ampEnv.s", range: [0, 1], label: "Amp S" },
  { leaf: "ampEnv.r", range: [0.001, 4], label: "Amp R" },
  { leaf: "modEnv.a", range: [0.001, 2], label: "Mod A" },
  { leaf: "modEnv.d", range: [0.001, 2], label: "Mod D" },
  { leaf: "modEnv.s", range: [0, 1], label: "Mod S" },
  { leaf: "modEnv.r", range: [0.001, 4], label: "Mod R" },
  { leaf: "lfo.rateHz", range: [0.01, 30], label: "LFO rate" },
  { leaf: "lfo.depth", range: [0, 1], label: "LFO depth" },
  { leaf: "lfo2.rateHz", range: [0.01, 30], label: "LFO2 rate" },
  { leaf: "lfo2.depth", range: [0, 1], label: "LFO2 depth" },
  { leaf: "granular.grainSizeMs", range: [5, 200], label: "Grain ms" },
  { leaf: "granular.density", range: [0.5, 32], label: "Grain density" },
  { leaf: "granular.spray", range: [0, 1], label: "Grain spray" },
  { leaf: "granular.stretch", range: [0.1, 4], label: "Stretch" },
];

/** Shared pad fields; mode-specific leaves also listed. */
const DRUM_PAD_LEAVES: Array<{ suffix: string; range: [number, number]; label: string }> = [
  { suffix: "level", range: [0, 1], label: "Level" },
  { suffix: "pitchSemis", range: [-24, 24], label: "Pitch semis" },
  { suffix: "ampEnv.a", range: [0.001, 2], label: "Amp A" },
  { suffix: "ampEnv.d", range: [0.001, 2], label: "Amp D" },
  { suffix: "ampEnv.s", range: [0, 1], label: "Amp S" },
  { suffix: "ampEnv.r", range: [0.001, 2], label: "Amp R" },
  { suffix: "pitchHz", range: [20, 8000], label: "Pitch Hz" },
  { suffix: "pitchEnv", range: [0, 4], label: "Pitch env" },
  { suffix: "decayMs", range: [10, 2000], label: "Decay ms" },
  { suffix: "toneLevel", range: [0, 1], label: "Tone" },
  { suffix: "noiseLevel", range: [0, 1], label: "Noise" },
  { suffix: "clickLevel", range: [0, 1], label: "Click" },
  { suffix: "filterHz", range: [200, 18000], label: "Filter Hz" },
];

const SF2_LEAVES: InstrumentPresetLeaf[] = [
  { leaf: "gain", range: [0, 1], label: "Gain" },
];

function toMap(leaves: InstrumentPresetLeaf[]): Map<string, InstrumentPresetLeaf> {
  return new Map(leaves.map(entry => [entry.leaf, entry]));
}

const BY_KIND: Record<InstrumentLeafKind, Map<string, InstrumentPresetLeaf>> = {
  synth: toMap(SYNTH_LEAVES),
  wavetable: toMap(WAVETABLE_LEAVES),
  sampler: toMap(SAMPLER_LEAVES),
  drums: new Map(),
  sf2: toMap(SF2_LEAVES),
};

function resolveRouteDepth(dottedLeaf: string): InstrumentPresetLeaf | null {
  const match = /^routes\.(\d+)\.depth$/.exec(dottedLeaf);
  if (!match) {
    return null;
  }
  const index = Number(match[1]);
  return {
    leaf: dottedLeaf,
    range: [-2, 2],
    label: `Mod ${index + 1} depth`,
  };
}

function resolveDrumPadLeaf(dottedLeaf: string): InstrumentPresetLeaf | null {
  const match = /^pads\.(\d+)\.(.+)$/.exec(dottedLeaf);
  if (!match) {
    return null;
  }
  const pad = Number(match[1]);
  const suffix = match[2]!;
  const meta = DRUM_PAD_LEAVES.find(entry => entry.suffix === suffix);
  if (!meta) {
    return null;
  }
  return {
    leaf: dottedLeaf,
    range: meta.range,
    label: `Pad ${pad + 1} ${meta.label}`,
  };
}

export function normalizeInstrumentLeafKind(
  kind: string | null | undefined,
): InstrumentLeafKind | null {
  if (
    kind === "synth"
    || kind === "wavetable"
    || kind === "sampler"
    || kind === "drums"
    || kind === "sf2"
  ) {
    return kind;
  }
  return null;
}

export function dottedPresetLeafFromSlashParts(parts: string[]): string | null {
  if (parts.length === 0) {
    return null;
  }
  return parts.join(".");
}

export function slashPresetLeafFromDotted(leaf: string): string {
  return leaf.split(".").filter(Boolean).join("/");
}

/** Public learn path for dock controls (`path` prop is dotted). */
export function instrumentPresetLearnPath(slot: number, dottedLeaf: string): string {
  return `/track/${slot}/instrument/preset/${slashPresetLeafFromDotted(dottedLeaf)}`;
}

/** @deprecated Prefer resolveInstrumentLeaf — kept for older call sites. */
export function resolveSynthPresetLeaf(dottedLeaf: string): InstrumentPresetLeaf | null {
  return resolveInstrumentLeaf("synth", dottedLeaf);
}

export function resolveInstrumentLeaf(
  kind: string | null | undefined,
  dottedLeaf: string,
): InstrumentPresetLeaf | null {
  const normalized = normalizeInstrumentLeafKind(kind);
  if (normalized === "drums") {
    return resolveDrumPadLeaf(dottedLeaf);
  }
  if (normalized) {
    const known = BY_KIND[normalized].get(dottedLeaf);
    if (known) {
      return known;
    }
    if (normalized === "synth" || normalized === "wavetable" || normalized === "sampler") {
      return resolveRouteDepth(dottedLeaf);
    }
    return null;
  }
  // Unknown slot kind (tests / empty project): search any catalog.
  for (const map of Object.values(BY_KIND)) {
    const hit = map.get(dottedLeaf);
    if (hit) {
      return hit;
    }
  }
  return resolveRouteDepth(dottedLeaf) ?? resolveDrumPadLeaf(dottedLeaf);
}

export function listSynthPresetLeaves(): InstrumentPresetLeaf[] {
  return SYNTH_LEAVES.slice();
}

export function setPresetLeaf(
  root: Record<string, unknown>,
  dottedLeaf: string,
  value: unknown,
): void {
  const keys = dottedLeaf.split(".").filter(Boolean);
  if (keys.length === 0) {
    return;
  }
  let cursor: Record<string, unknown> = root;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i]!;
    const next = cursor[key];
    if (!next || typeof next !== "object") {
      cursor[key] = {};
    }
    cursor = cursor[key] as Record<string, unknown>;
  }
  cursor[keys[keys.length - 1]!] = value;
}

/** Read a dotted numeric leaf (e.g. `filter.cutoffHz`) from a preset/kit object. */
export function getPresetLeaf(
  root: Record<string, unknown>,
  dottedLeaf: string,
): number | null {
  const keys = dottedLeaf.split(".").filter(Boolean);
  let cursor: unknown = root;
  for (const key of keys) {
    if (!cursor || typeof cursor !== "object") {
      return null;
    }
    cursor = (cursor as Record<string, unknown>)[key];
  }
  return typeof cursor === "number" && Number.isFinite(cursor) ? cursor : null;
}
